'use client';

import { useCallback, useState } from 'react';

import type {
  ActionKitErrorBase,
  InferActionKitErrorCodes,
  InferActionKitErrorTypeForCode,
  ActionKitResult,
  ActionKitErrorUnknown,
  ActionKitAction2,
  UseActionKitOptions2,
} from './types';

export function useActionKit<
  Params extends any[],
  Return,
  TError extends ActionKitErrorBase = ActionKitErrorUnknown
>(
  action: ActionKitAction2<Params, Return, TError>,
  options: UseActionKitOptions2<TError | ActionKitErrorUnknown, Params, Return>,
): {
  state: ActionKitResult<Return, TError | ActionKitErrorUnknown> | undefined;
  loading: boolean;
  action: (
    ...args: Params
  ) => Promise<ActionKitResult<Return, TError | ActionKitErrorUnknown>>;
} {
  type Success = Return;
  type Result = ActionKitResult<Success, TError | ActionKitErrorUnknown>;
  type Args = Params;

  const { onSuccess, onError: onErrorProp } = options;

  const [state, setState] = useState<Result>();
  const [loading, setLoading] = useState(false);
  const actionCallback = useCallback(
    async (...args: Args) => {
      setLoading(true);
      try {
        const result = await action(...args);

        if (result.success) {
          await onSuccess?.(result.data, ...args);
          setState(result);
          setLoading(false);
          return result;
        }

        const errorCode = result.data.code as InferActionKitErrorCodes<TError>;
        const errorHandler = onErrorProp[errorCode];
        await errorHandler(
          result.data as InferActionKitErrorTypeForCode<
            TError,
            typeof errorCode
          >,
        );
        setState(result);
        setLoading(false);
        return result;
      } catch (error) {
        const state = {
          success: false as const,
          data: { code: 'unknown', data: error },
        } as ActionKitResult<Success, ActionKitErrorUnknown, false>;
        const errorHandler = onErrorProp['unknown' as const];
        await errorHandler(
          state.data as InferActionKitErrorTypeForCode<TError, 'unknown'>,
        );
        setState(state);
        setLoading(false);
        return state;
      }
    },
    [action, onSuccess, onErrorProp],
  );

  return {
    state,
    loading,
    action: actionCallback,
  };
}
